import dayjs from 'dayjs';
import { OperationalPeriod } from '../components/IAP/hooks/incidentHooks';

/** Compact header chip timestamp (e.g. "Jul 17, 1:28 PM"). */
export function formatCompactOpTimestamp(
  value: string | Date | dayjs.Dayjs | null | undefined
): string {
  if (value == null || value === '') return '—';
  const date = dayjs(value);
  if (!date.isValid()) return '—';
  return date.format('MMM D, h:mm A');
}

export const parsePeriodDates = (period: OperationalPeriod) => {
  const periodStartDate = period.start_date_time
    ? dayjs(period.start_date_time)
    : null;
  const periodEndDate = dayjs(
    period.anticipated_end_date_time || period.end_date_time
  );

  return { periodStartDate, periodEndDate };
};

export const isDateRangeInPeriod = (
  startDate: dayjs.Dayjs | Date | string,
  endDate: dayjs.Dayjs | Date | string,
  period: OperationalPeriod
): boolean => {
  const { periodStartDate, periodEndDate } = parsePeriodDates(period);
  const rangeStart = dayjs(startDate);
  const rangeEnd = dayjs(endDate);

  const rangeEndsAfterOrOnPeriodStart =
    rangeEnd.isAfter(periodStartDate) || rangeEnd.isSame(periodStartDate);
  const rangeStartsBeforeOrOnPeriodEnd =
    rangeStart.isBefore(periodEndDate) || rangeStart.isSame(periodEndDate);

  return rangeEndsAfterOrOnPeriodStart && rangeStartsBeforeOrOnPeriodEnd;
};

const getMostRecentItem = <T>(
  items: T[],
  getDate: (item: T) => dayjs.Dayjs | Date | string | null | undefined
): T => {
  return items.reduce((latest, current) => {
    const latestDate = dayjs(getDate(latest));
    const currentDate = dayjs(getDate(current));
    return currentDate.isAfter(latestDate) ? current : latest;
  });
};

export const filterItemsByOperationalPeriod = <T>(
  items: T[] | undefined,
  currentPeriod: OperationalPeriod | undefined,
  allPeriods: OperationalPeriod[] | undefined,
  getDate: (item: T) => dayjs.Dayjs | Date | string | null | undefined
): T | null => {
  if (!items || items.length === 0) return null;
  if (!currentPeriod) {
    return null;
  }

  const nextPeriod = allPeriods?.find(
    (period) => period.period_number === (currentPeriod.period_number || 0) + 1
  );
  const currentPeriodStart = dayjs(currentPeriod.start_date_time);
  const nextPeriodStart = nextPeriod ? dayjs(nextPeriod.start_date_time) : null;

  const itemsInPeriod = items.filter((item) => {
    const itemDate = getDate(item);
    if (!itemDate) return false;

    const itemDateDayjs = dayjs(itemDate);
    const isAfterCurrentPeriodStart =
      itemDateDayjs.isAfter(currentPeriodStart) ||
      itemDateDayjs.isSame(currentPeriodStart);
    if (!isAfterCurrentPeriodStart) {
      return false;
    }
    if (nextPeriodStart) {
      return itemDateDayjs.isBefore(nextPeriodStart);
    }
    return true;
  });

  if (itemsInPeriod.length === 0) {
    return null;
  }

  if (itemsInPeriod.length === 1) {
    return itemsInPeriod[0];
  }

  return getMostRecentItem(itemsInPeriod, getDate);
};
